import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d71ttpk991ove783t6qg';

const productOptions = [
  // Ceramic Coatings
  'Ceramic Plus 10H Radical™',
  'BOROGRAPH 3.0 "Stealth" CERAMIC COATING',
  'Borophene 3.0',
  'Borophene 4.0 GRAND MASTER',
  'REFRESH™ - Hydrophobic Spray for Cars',
  'Ceramic Coating For RV Gelcoat - 50 ML Sio3 Ceramic Coating',
  // Specialty Coatings
  'Black Pearl Ceramic Coating',
  'Soft Fabric Sio3™ Ceramic Coating - Car Interior (50mL)',
  'Matte Plus Ceramic Coating for Satin & Matte',
  'Nanopro® "SOLAR CHARGE" CERAMIC COATING VERSION',
  'Carpet Magic E-Z Spray™ on Application',
  'Ceramic Coating for Wheels (Wheel & Caliper™ Ceramic Coating) - 50 mL',
  'Nanoflex™ Plastic & Rubber™ (Tire) Ceramic Coating for Cars',
  'EZ Glass™ - Ceramic Coating for Glass',
  'Everything Leather™ - Interior Ceramic Coating',
  'Ceramic Soap™ - The Advance Ceramic Coating Solutions - 8 oz',
  'Ceramic Coating for Vinyl Convertible™ Tops and Soft Canvas Tops E-Z Spray on Application',
  'Nanopro® CLEARPRO- Hand Applied Clear coat',
  // Liquid PPF
  'NANOPRO LIQUID PAINT PROTECTION FILM KIT (LPF ACCELERATE™)',
  // Color Change PPF
  'NANOPRO LIQUID PAINT PROTECTION FILM KIT Color Change',
  'NANOPRO® MATTE LPF - LIQUID PPF FOR MATTE/SATIN VEHICLE PAINTJOBS',
  // Window Tint
  'MEDIUM - W.O.W. (WIPE ON WINDOW TINT) BY NANOPRO®',
  // Marine Coatings
  'Nautilus 250mL Marine Sio3™ Ceramic Coating - Ceramic Coating For Marine',
  'Wood Plus™ Ceramic Coating',
  // General
  'General Inquiry / Not Sure Yet',
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

const ContactSection = () => {
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.querySelector('textarea[name="message"]') as HTMLTextAreaElement)?.value || '';
    if (msg.length > 500) return;
    setStatus('submitting');
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([k, v]) => data.append(k, String(v)));
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <section id="contact" className="relative bg-[#060606] py-24 lg:py-32 border-t border-white/5 overflow-hidden">
      {/* Ghost headline backdrop */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span
          className="font-black text-white/[0.025] select-none whitespace-nowrap"
          style={{ fontSize: 'clamp(80px, 14vw, 190px)', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.04em' }}
        >
          REQUEST INFO
        </span>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-14">

        {/* Top accent bar */}
        <div className="h-[2px] w-20 bg-[#c9a84c] mb-10" />

        <div className="grid lg:grid-cols-[1fr_1.45fr] gap-14 lg:gap-20 items-start">

          {/* ── Left info panel ── */}
          <div className="lg:sticky lg:top-28">
            <p className="text-[#c9a84c] text-[11px] tracking-[0.3em] font-bold mb-5">GET IN TOUCH</p>
            <h2
              className="text-white font-black leading-[1.0] mb-7"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(32px, 4.2vw, 52px)', letterSpacing: '-0.025em' }}
            >
              Request Product<br />
              <span className="text-gold-gradient">Information</span>
            </h2>
            <p className="text-white/42 text-[14px] leading-[1.85] mb-10">
              Contact Surface Solutions of CT for product inquiries, dealer purchasing assistance, availability checks,
              or guidance on selecting the right NanoPro® ceramic coating for your application.
            </p>

            {/* Contact details */}
            <div className="flex flex-col gap-6 mb-10">
              {[
                {
                  icon: 'ri-map-pin-2-fill',
                  label: 'NANOPRO® CORPORATE HQ',
                  lines: ['3549 High Ridge Rd', 'Boynton Beach, FL 33426', 'United States'],
                },
                {
                  icon: 'ri-phone-fill',
                  label: 'HQ PHONE',
                  lines: ['561-631-4085'],
                  href: 'tel:5616314085',
                },
                {
                  icon: 'ri-mail-fill',
                  label: 'EMAIL',
                  lines: ['contact@nanoproceramic.com'],
                  href: 'mailto:contact@nanoproceramic.com',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center border border-[#c9a84c]/25 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#c9a84c] text-[15px]`} />
                  </div>
                  <div>
                    <p className="text-white/28 text-[10px] tracking-[0.18em] uppercase mb-1.5">{item.label}</p>
                    {item.lines.map((line) =>
                      item.href ? (
                        <a key={line} href={item.href} className="block text-white/65 text-[14px] font-semibold hover:text-[#c9a84c] transition-colors cursor-pointer">
                          {line}
                        </a>
                      ) : (
                        <p key={line} className="text-white/60 text-[13px]">{line}</p>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Authorized badge */}
            <div className="flex items-center gap-4 p-5 border border-[#c9a84c]/18 bg-[#c9a84c]/5">
              <i className="ri-verified-badge-fill text-[#c9a84c] text-[24px] flex-shrink-0" />
              <div>
                <p className="text-[#c9a84c] text-[12px] font-black tracking-[0.1em]">Surface Solutions of CT</p>
                <p className="text-white/42 text-[12px]">Authorized Reseller — Nano Pro Ceramic®</p>
              </div>
            </div>
          </div>

          {/* ── Right form panel ── */}
          <div className="border border-white/8 bg-[#0c0c0c]">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-20 px-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center border border-[#c9a84c]/40 mb-6">
                  <i className="ri-check-line text-[#c9a84c] text-[32px]" />
                </div>
                <h3 className="text-white font-black text-[22px] mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Inquiry Received
                </h3>
                <p className="text-white/42 text-[14px] max-w-[300px] leading-relaxed mb-8">
                  Thank you for reaching out. A Surface Solutions of CT representative will follow up with you shortly.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-6 py-3 border border-white/14 text-white/55 text-[12px] tracking-[0.1em] hover:border-[#c9a84c] hover:text-[#c9a84c] transition-colors cursor-pointer whitespace-nowrap"
                >
                  SEND ANOTHER INQUIRY
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="p-8 lg:p-10 flex flex-col gap-6">
                <div className="flex items-center gap-3 pb-6 border-b border-white/6">
                  <i className="ri-file-text-line text-[#c9a84c] text-[20px]" />
                  <div>
                    <p className="text-white font-bold text-[15px]">Product Inquiry Form</p>
                    <p className="text-white/35 text-[12px]">All fields with * are required</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Full Name *</label>
                    <input
                      name="name" required placeholder="Your name"
                      className="bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm placeholder-white/18 focus:outline-none focus:border-[#c9a84c]/40 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Company</label>
                    <input
                      name="company" placeholder="Company name"
                      className="bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm placeholder-white/18 focus:outline-none focus:border-[#c9a84c]/40 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Email Address *</label>
                    <input
                      name="email" type="email" required placeholder="your@email.com"
                      className="bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm placeholder-white/18 focus:outline-none focus:border-[#c9a84c]/40 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Phone Number</label>
                    <input
                      name="phone" type="tel" placeholder="(000) 000-0000"
                      className="bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm placeholder-white/18 focus:outline-none focus:border-[#c9a84c]/40 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Product of Interest</label>
                  <div className="relative">
                    <select
                      name="products_interest"
                      className="w-full bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c9a84c]/40 transition-colors appearance-none cursor-pointer pr-10"
                    >
                      <option value="">Select a product or category...</option>
                      {productOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-white/38 text-[10px] tracking-[0.18em] uppercase">Message / Additional Details</label>
                  <textarea
                    name="message" rows={4} maxLength={500}
                    placeholder="Surface type, application, quantity needs, or any questions..."
                    className="bg-[#070707] border border-white/10 px-4 py-3 text-white text-sm placeholder-white/18 focus:outline-none focus:border-[#c9a84c]/40 transition-colors resize-none"
                  />
                  <p className="text-white/22 text-[10px]">Max 500 characters</p>
                </div>

                {status === 'error' && (
                  <p className="text-red-400/80 text-[12px]">Something went wrong. Please try again or contact us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-4 bg-[#c9a84c] text-[#060606] text-[13px] font-black tracking-[0.18em] hover:bg-[#e0bf6a] disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap mt-1"
                >
                  {status === 'submitting' ? 'SENDING...' : 'REQUEST PRODUCT INFORMATION'}
                </button>

                <p className="text-white/22 text-[11px] text-center leading-relaxed">
                  No pricing, no cart. This is a direct inquiry to Surface Solutions of CT — your authorized NanoPro® reseller.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
